import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ChevronRight, 
  ChevronLeft, 
  Loader2, 
  Target, 
  Dumbbell, 
  Scale,
  User,
  Trophy
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dfed4268-137d-4b89-bcab-122f53e08876/artifacts/k5gcf49q_BBA%20LOGO.webp";

// Custom Logo Component with clean styling
const Logo = ({ className = "h-16 w-auto" }) => (
  <div className={`relative ${className}`} style={{ aspectRatio: '1/1' }}>
    <img 
      src={LOGO_URL} 
      alt="Bodies By Akeem" 
      className="h-full w-auto rounded-full"
      style={{ 
        background: 'transparent',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
      }}
    />
  </div>
);

const fitnessGoals = [
  { value: 'weight_loss', label: 'Weight Loss', icon: '🔥', desc: 'Burn fat and get lean' },
  { value: 'muscle_gain', label: 'Build Muscle', icon: '💪', desc: 'Gain strength and size' },
  { value: 'endurance', label: 'Improve Endurance', icon: '🏃', desc: 'Boost cardiovascular fitness' },
  { value: 'flexibility', label: 'Flexibility', icon: '🧘', desc: 'Increase mobility and balance' },
  { value: 'general_fitness', label: 'General Fitness', icon: '⭐', desc: 'Overall health improvement' }
];

const fitnessLevels = [
  { value: 'beginner', label: 'Beginner', desc: 'New to fitness or returning after a break' },
  { value: 'intermediate', label: 'Intermediate', desc: '6+ months of consistent training' },
  { value: 'advanced', label: 'Advanced', desc: '2+ years of dedicated training' }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { token, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    age: '',
    height_cm: '',
    weight_kg: '',
    fitness_goal: '',
    fitness_level: '',
    body_fat_percentage: null
  });

  const totalSteps = 4;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/onboarding`,
        {
          age: parseInt(formData.age),
          height_cm: parseFloat(formData.height_cm),
          weight_kg: parseFloat(formData.weight_kg),
          fitness_goal: formData.fitness_goal,
          fitness_level: formData.fitness_level,
          body_fat_percentage: formData.body_fat_percentage ? parseFloat(formData.body_fat_percentage) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile completed! Welcome to Bodies By Akeem!');
      updateUser({ onboarding_completed: true });
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save profile. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.age && formData.height_cm && formData.weight_kg;
      case 2:
        return formData.fitness_goal;
      case 3:
        return formData.fitness_level;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo className="h-16" />
        </div>
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Step {step} of {totalSteps}</span>
            <span className="text-sm text-primary font-medium">{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>
        
        {/* Step Content */}
        <div className="card-tactical">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-2xl text-white uppercase">Tell Us About Yourself</h2>
                <p className="text-gray-400 mt-2">We'll use this to personalize your experience</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Age</Label>
                  <Input
                    type="number"
                    data-testid="onboarding-age-input"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="input-tactical bg-secondary border-transparent"
                    placeholder="25"
                    min="13"
                    max="100"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Height (cm)</Label>
                  <Input
                    type="number"
                    data-testid="onboarding-height-input"
                    value={formData.height_cm}
                    onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                    className="input-tactical bg-secondary border-transparent"
                    placeholder="175"
                    min="100"
                    max="250"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Weight (kg)</Label>
                  <Input
                    type="number"
                    data-testid="onboarding-weight-input"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                    className="input-tactical bg-secondary border-transparent"
                    placeholder="70"
                    min="30"
                    max="300"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Fitness Goal */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-2xl text-white uppercase">What's Your Goal?</h2>
                <p className="text-gray-400 mt-2">Select your primary fitness objective</p>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                {fitnessGoals.map((goal) => (
                  <button
                    key={goal.value}
                    data-testid={`onboarding-goal-${goal.value}`}
                    onClick={() => setFormData({ ...formData, fitness_goal: goal.value })}
                    className={`flex items-center gap-4 p-4 rounded-sm border transition-all ${
                      formData.fitness_goal === goal.value 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-secondary/50 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-2xl">{goal.icon}</span>
                    <div className="text-left">
                      <div className="text-white font-medium">{goal.label}</div>
                      <div className="text-sm text-gray-400">{goal.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Step 3: Fitness Level */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Dumbbell className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-2xl text-white uppercase">Your Fitness Level</h2>
                <p className="text-gray-400 mt-2">Be honest - we'll customize workouts accordingly</p>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                {fitnessLevels.map((level) => (
                  <button
                    key={level.value}
                    data-testid={`onboarding-level-${level.value}`}
                    onClick={() => setFormData({ ...formData, fitness_level: level.value })}
                    className={`flex items-center gap-4 p-4 rounded-sm border transition-all ${
                      formData.fitness_level === level.value 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-secondary/50 hover:border-gray-600'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                      formData.fitness_level === level.value ? 'bg-primary' : 'bg-secondary'
                    }`}>
                      <Dumbbell className={`w-6 h-6 ${
                        formData.fitness_level === level.value ? 'text-white' : 'text-gray-400'
                      }`} />
                    </div>
                    <div className="text-left">
                      <div className="text-white font-medium">{level.label}</div>
                      <div className="text-sm text-gray-400">{level.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Step 4: Body Composition (Optional) */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-2xl text-white uppercase">Body Composition</h2>
                <p className="text-gray-400 mt-2">Optional - helps us track your progress better</p>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-gray-300">Body Fat Percentage (Optional)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      data-testid="onboarding-bodyfat-slider"
                      value={[formData.body_fat_percentage || 20]}
                      onValueChange={([value]) => setFormData({ ...formData, body_fat_percentage: value })}
                      min={5}
                      max={50}
                      step={1}
                      className="flex-1"
                    />
                    <span className="font-mono text-xl text-white w-16 text-right">
                      {formData.body_fat_percentage || 20}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Don't know? That's okay! You can skip this or update it later.
                  </p>
                </div>
                
                <div className="p-4 bg-secondary/50 rounded-sm border border-border">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-8 h-8 text-primary" />
                    <div>
                      <div className="text-white font-medium">You're almost ready!</div>
                      <div className="text-sm text-gray-400">
                        Complete your profile to earn your first badge
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button
                data-testid="onboarding-next-btn"
                onClick={handleNext}
                disabled={!isStepValid()}
                className="bg-primary hover:bg-red-700 text-white"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="onboarding-complete-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-primary hover:bg-red-700 text-white"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Complete Profile
                    <Trophy className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
